import { useEffect, useState } from "react";
import Drawer from "@mui/material/Drawer";
import { Box, FormGroup } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";
import moment from "moment-timezone";

import { CustomCheckbox } from "./checkBox/CheckBox";
import Text from "./text/Text";
import {
  apply,
  assets as assetsTxt,
  enterMinRequiredDate,
  enterValidDateMsg,
  enterValidateFutureDate,
  filter,
  greaterEndDateMsg,
  type,
  clear,
} from "./messages";
import CustomDivider from "./divider/Divider";
import CustomDateRangePicker from "./datepicker/CustomDateRangePicker";
import Button from "./button/Button";
import {
  isFutureDate,
  isTMA,
  isValidDate,
  minDateYear,
  minReqYear,
  transactionTypeData,
} from "./constants";

function FilterDrawer({
  open,
  handleDrawerClose,
  handleApplyFilter,
  isFromBrowser,
}) {
  const [disabled, setDisabled] = useState(true);

  const { accountAssets, transactionFilterData } = useSelector(
    (state) => state.common
  );

  const validateDateRange = yup.object({
    start_at: yup
      .mixed()
      .test("start_at", function (value) {
        const validation = validateFilterStartDate(value, this.parent);
        if (!validation.isValid) {
          return this.createError({
            path: this.path,
            message: validation.errorMessage,
          });
        } else {
          return true;
        }
      })
      .notRequired(),
    end_at: yup
      .mixed()
      .test("end_at", function (value) {
        const validation = validateFilterEndDate(value, this.parent);

        if (!validation.isValid) {
          return this.createError({
            path: this.path,
            message: validation.errorMessage,
          });
        } else {
          return true;
        }
      })
      .notRequired(),
  });

  const formik = useFormik({
    initialValues: {
      assets: [],
      types: [],
      start_at: null,
      end_at: null,
    },
    validationSchema: validateDateRange,
    enableReinitialize: true,
  });

  const { values, isValid, dirty, setFieldValue } = formik;

  const validateSelectedDate = (dateObj, isStartDate, formikObj) => {
    const isInvalidDate = !isValidDate(dateObj);

    // Check selected date is valid or not
    if (isInvalidDate) {
      return {
        isValid: false,
        errorMessage: enterValidDateMsg(isStartDate),
      };
    }
    //Check Selected date must be after the minimum year
    const isInvalidMinYear = moment(dateObj).diff(minDateYear) < 0;

    if (isInvalidMinYear) {
      return {
        isValid: false,
        errorMessage: enterMinRequiredDate(isStartDate, minReqYear),
      };
    }

    //Check selected date must not be the future date
    const isFuture = isFutureDate(dateObj);
    if (isFuture) {
      return {
        isValid: false,
        errorMessage: enterValidateFutureDate(isStartDate),
      };
    }

    if (!isStartDate) {
      const dateDifference = moment(dateObj).diff(moment(formikObj?.start_at));
      if (dateDifference < 0) {
        return {
          isValid: false,
          errorMessage: greaterEndDateMsg,
        };
      }
    }

    return {
      isValid: true,
    };
  };

  const validateFilterStartDate = (value) => {
    if (value) {
      return validateSelectedDate(value, true);
    } else {
      return { isValid: true };
    }
  };

  const validateFilterEndDate = (value, formikObj) => {
    if (value) {
      return validateSelectedDate(value, false, formikObj);
    } else {
      return { isValid: true };
    }
  };

  useEffect(() => {
    setFieldValue("assets", transactionFilterData?.target_currency?.in || []);
    setFieldValue("types", transactionFilterData?.type?.in || []);
    setFieldValue("start_at", +transactionFilterData?.created?.gte || null);
    setFieldValue("end_at", +transactionFilterData?.created?.lte || null);
  }, [transactionFilterData]);

  useEffect(() => {
    setDisabled(!(isValid && dirty));
  }, [dirty, isValid, values]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleDrawerClose}
      className={`filter-drawer-wrapper ${
        isFromBrowser ? "options-width" : "popup-width"
      }`}
      ModalProps={{
        disablePortal: !isTMA(),
      }}
    >
      <Box className="drawer-header">
        <ArrowBackIcon
          sx={{ cursor: "pointer" }}
          onClick={() => handleDrawerClose()}
        ></ArrowBackIcon>
        <Text className="default-text ml-8" size={18} font="bold">
          {filter}
        </Text>
      </Box>
      <Box className="drawer-body" p="20px">
        <Text
          className="default-text"
          size={16}
          font="bold"
          sx={{ marginBottom: "16px" }}
        >
          {assetsTxt}
        </Text>
        <FormGroup className="check-box-data">
          {accountAssets?.map((option) => (
            <Box className="checkbox-items" key={option?.id}>
              <Box className="checkbox">
                <CustomCheckbox
                  key={option?.id}
                  checked={(values.assets || []).includes(option?.uuid)}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    const current = values.assets || [];
                    const next = isChecked
                      ? [...current, option?.uuid]
                      : current.filter((v) => v !== option?.uuid);
                    setFieldValue("assets", next);
                  }}
                  label=""
                />
              </Box>
              <Box>
                <Text
                  className="default-text"
                  font="regular"
                  size={16}
                  variant="caption"
                >
                  {option?.name}
                </Text>
              </Box>
            </Box>
          ))}
        </FormGroup>

        <CustomDivider sx={{ margin: "14px -20px" }} />
        <Text
          className="default-text"
          size={16}
          font="bold"
          sx={{ marginBottom: "16px" }}
        >
          {type}
        </Text>
        <FormGroup className="check-box-data">
          {transactionTypeData.map((option) => (
            <Box className="checkbox-items" key={option?.id}>
              <Box className="checkbox">
                <CustomCheckbox
                  key={option.id}
                  disabled={!transactionTypeData.length}
                  checked={(values.types || []).includes(option?.short_name)}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    const current = values.types || [];
                    const next = isChecked
                      ? [...current, option?.short_name]
                      : current.filter((v) => v !== option?.short_name);
                    setFieldValue("types", next);
                  }}
                  label=""
                />
              </Box>
              <Box>
                <Text
                  className="default-text"
                  font="regular"
                  size={16}
                  variant="caption"
                >
                  {option?.label ? option.label : option.short_name}
                </Text>
              </Box>
            </Box>
          ))}
        </FormGroup>

        <CustomDivider sx={{ margin: "14px -20px" }} />
        <CustomDateRangePicker formik={formik} />
      </Box>
      <Box
        className="drawer-footer"
        p="14px 20px"
        display="flex"
        justifyContent="space-between"
        border="1px solid #F6F3F3"
      >
        <Button
          variant="outlined"
          className="submit-btn"
          label={clear}
          onClick={() => {
            handleDrawerClose();
          }}
          sx={{ width: "48%" }}
        />
        <Button
          variant="contained"
          className="submit-btn"
          label={apply}
          onClick={() => {
            handleDrawerClose();
            handleApplyFilter(values);
          }}
          sx={{ width: "48%" }}
          disabled={disabled}
        />
      </Box>
    </Drawer>
  );
}

export default FilterDrawer;
